import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components/macro';
import { NavLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
import BlogPanel from '@tovia/man-ui/lib/components/Blog/BlogPanel';
import Comment from '@tovia/man-ui/lib/components/Comments/Comment';
import LoaderWrapper from '../Loader/LoaderWrapper';

export const CommentsPanelComponent = (props) => {
  const { className, comments, error, expand, expanded, isUser, load, loading, showUpgradeDialog } = props;

  const showShareDialog = () => {};

  const intl = useIntl();

  // eslint-disable-next-line react/prop-types
  const renderCommentLink = ({ linkText, link, timestamp }) => {
    if (link && linkText) {
      const date = intl.formatDate(new Date(timestamp), { year: 'numeric', month: 'long', day: '2-digit' });
      const on = intl.formatMessage({ id: 'comments.blogPanel.on', defaultMessage: 'on' });

      return (
        <div className="view-link">
          <span>{date}</span>
          {` ${on} `}
          <NavLink to={link}>{linkText}</NavLink>
        </div>
      );
    }
    return null;
  };

  return (
    <BlogPanel
      className={className}
      headline={intl.formatMessage({ id: 'comments.blogPanel.title', defaultMessage: 'Latest Comments' })}
    >
      <ul className="list-unstyled">
        {comments.map((comment) => (
          <Comment
            badge={comment.badge}
            displayName={comment.displayName}
            isRated={comment.isRated}
            isUser={isUser}
            key={`latest_${comment.UUID.substring(0, 6)}`}
            link={renderCommentLink(comment)}
            onRate={comment.onRate}
            ratesCount={comment.ratesCount || 0}
            showShareDialog={showShareDialog}
            showUpgradeDialog={showUpgradeDialog}
            text={comment.text.length > 120 ? `${comment.text.substring(0, 120)}...` : comment.text}
            timestamp={comment.timestamp}
            UUID={comment.UUID}
          />
        ))}
        {!loading && !error && comments.length === 0 && (
          <div>
            {intl.formatMessage({
              id: 'comments.blogPanel.noRecentComments',
              defaultMessage: 'There are no recent comments',
            })}
          </div>
        )}
      </ul>
      {!loading && !error && !expanded && (
        <div>
          <a className="btn-load-more btn btn-default clickable" onClick={expand}>
            {intl.formatMessage({ id: 'comments.blogPanel.button', defaultMessage: 'LOAD MORE' })}
          </a>
        </div>
      )}
      {(loading || error) && (
        <div>
          <LoaderWrapper
            contentType="Latest Comments"
            loadingStatus={loading}
            reloadFunction={expanded ? expand : load}
            reduxErrorStatus={error}
          />
        </div>
      )}
    </BlogPanel>
  );
};

CommentsPanelComponent.propTypes = {
  className: PropTypes.string,
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      badge: PropTypes.shape({
        icon: PropTypes.object,
        title: PropTypes.string,
      }),
      displayName: PropTypes.string,
      isRated: PropTypes.bool,
      link: PropTypes.string,
      linkText: PropTypes.string,
      onRate: PropTypes.func.isRequired,
      ratesCount: PropTypes.number,
      text: PropTypes.string,
      timestamp: PropTypes.string,
      UUID: PropTypes.string,
    }),
  ),
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  expand: PropTypes.func.isRequired,
  expanded: PropTypes.bool,
  isUser: PropTypes.bool,
  load: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  showUpgradeDialog: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
};

CommentsPanelComponent.defaultProps = {
  className: '',
  comments: [],
  isUser: false,
  error: null,
  expanded: false,
  loading: false,
};

export default withTheme(styled(CommentsPanelComponent)`
  & .btn-load-more {
    background: ${(props) => props.theme.primary1};
    border-color: ${(props) => props.theme.primary3};
    border-radius: 0;
    color: ${(props) => props.theme.primary4};
    margin: 20px 0;
    padding: 10px 110px;
    width: 100%;

    @media (max-width: 374px) {
      padding: 10px 80px;
    }
  }

  & .view-link {
    font-size: 0.9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    margin-bottom: 8.5px;
    color: ${(props) => props.theme.primary7};

    span {
      text-transform: capitalize;
    }
  }

  & .comment-actions {
    color: ${(props) => props.theme.primary7};
    :hover {
      color: unset;
    }

    &.active .inner {
      font-weight: bold;
      color: ${(props) => props.theme.primaryBtn1Bg};
    }
  }
`);
